// src/DocumentsPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { X, Loader, FileText, Download, Calendar, Eye } from 'lucide-react';
import { API } from './utils/database-frontend.js';

const DocumentsPage = () => {
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const documentsRef = useRef(null);
  
  const isDocumentsInView = useInView(documentsRef, { once: true, threshold: 0.1 });

  // Fetch documents from database
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        const fetchedDocuments = await API.documents.getAll();
        console.log('Fetched documents from API:', fetchedDocuments);
        setDocuments(fetchedDocuments);
        setError(null);
      } catch (err) {
        console.error('Error fetching documents:', err);
        setError('Belgeler yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
        setDocuments([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, []);

  const openModal = (document) => {
    setSelectedDocument(document);
  };

  const closeModal = () => {
    setSelectedDocument(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Tarih belirtilmemiş';
    
    try {
      let date;
      if (typeof dateString === 'string') {
        if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
          date = new Date(dateString + 'T00:00:00');
        } else {
          date = new Date(dateString);
        }
      } else {
        date = new Date(dateString);
      }
      
      if (isNaN(date.getTime())) {
        console.error('Invalid date:', dateString);
        return 'Geçersiz tarih';
      }
      
      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Date formatting error:', error, dateString);
      return 'Tarih formatı hatası';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center bg-gradient-to-br from-brand-navy via-brand-gray to-brand-navy text-white overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent transform -skew-y-12"></div>
          <div className="absolute inset-0 bg-gradient-to-l from-transparent via-white to-transparent transform skew-y-12"></div>
        </div>
        
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-brand-orange rounded-full opacity-20"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -30, 0],
                opacity: [0.2, 0.5, 0.2],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>

        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <FileText className="w-20 h-20 mx-auto mb-6 text-brand-orange" />
            <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              Belgelerimiz
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-300 leading-relaxed">
              Kalite belgelerimiz, sertifikalarımız ve kurumsal dokümanlarımız
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <div className="bg-brand-orange bg-opacity-20 backdrop-blur-sm rounded-full px-6 py-3 border border-brand-orange border-opacity-30">
                <span className="text-white">Kalite Sertifikaları</span>
              </div>
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-full px-6 py-3 border border-white border-opacity-30">
                <span className="text-white">Kurumsal Belgeler</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-white border-opacity-50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white bg-opacity-50 rounded-full mt-2"></div>
          </div>
        </motion.div>
      </section>

      {/* Documents Section */}
      <section ref={documentsRef} className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isDocumentsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-brand-navy mb-6">
              Belgelerimiz
            </h2>
            <p className="text-xl text-brand-gray max-w-3xl mx-auto leading-relaxed">
              Kalite standartlarımızı belgeleyen sertifikalar ve kurumsal dokümanlarımızı inceleyebilirsiniz
            </p>
          </motion.div>

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <Loader className="w-8 h-8 animate-spin text-brand-orange" />
              <span className="ml-3 text-brand-gray">Belgeler yükleniyor...</span>
            </div>
          ) : error ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <p className="text-brand-gray text-lg mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="bg-brand-orange text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition duration-300"
              >
                Tekrar Dene
              </button>
            </motion.div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {documents.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="col-span-full text-center py-20"
                >
                  <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-brand-gray text-lg">Henüz belge eklenmemiş.</p>
                </motion.div>
              ) : (
                documents.map((document, index) => (
                  <motion.div
                    key={document.id}
                    initial={{ opacity: 0, y: 50, scale: 0.9 }}
                    animate={isDocumentsInView ? { opacity: 1, y: 0, scale: 1 } : {}}
                    transition={{ delay: index * 0.1, duration: 0.6 }}
                    whileHover={{ y: -8, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer group border border-gray-100"
                    onClick={() => openModal(document)}
                  >
                    <div className="relative overflow-hidden">
                      <img 
                        src={document.imageUrl || document.image_url || 'https://placehold.co/600x400/f4712f/ffffff?text=Belge+Görseli'} 
                        alt={document.title}
                        className="w-full h-72 object-contain bg-gradient-to-br from-gray-50 to-gray-100 group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          e.target.src = 'https://placehold.co/600x400/f4712f/ffffff?text=Görsel+Bulunamadı';
                        }}
                      />
                      <div className="absolute top-4 right-4 bg-brand-orange text-white px-3 py-1 rounded-full text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <Eye className="w-4 h-4 inline mr-1" />
                        Görüntüle
                      </div>
                    </div>
                    
                    <div className="p-6">
                      <div className="flex items-center text-sm text-brand-orange mb-3">
                        <Calendar className="w-4 h-4 mr-2" />
                        <span>{formatDate(document.createdAt || document.created_at)}</span>
                      </div>
                      
                      <h3 className="text-xl font-bold text-brand-navy mb-3 line-clamp-2 group-hover:text-brand-orange transition-colors duration-300">
                        {document.title}
                      </h3>
                      
                      <p className="text-brand-gray mb-4 line-clamp-3 leading-relaxed">
                        {document.description && document.description.length > 150 
                          ? document.description.substring(0, 150) + '...' 
                          : document.description || 'Açıklama mevcut değil'
                        }
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-brand-orange font-semibold text-sm">Kurumsal Belge</span>
                        <button className="text-brand-orange font-semibold flex items-center hover:text-orange-600 transition duration-300 group-hover:translate-x-1">
                          İncele
                          <Eye className="w-4 h-4 ml-1" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          )}
        </div>
      </section>

      {/* Modal */}
      <AnimatePresence>
        {selectedDocument && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-y-auto overflow-x-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header with Image */}
              <div className="relative">
                <div className="bg-gradient-to-br from-brand-navy to-brand-gray p-6 rounded-t-3xl">
                  <img 
                    src={selectedDocument.imageUrl || selectedDocument.image_url || 'https://placehold.co/600x400/f4712f/ffffff?text=Belge+Görseli'} 
                    alt={selectedDocument.title}
                    className="w-full h-96 object-contain bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl"
                    onError={(e) => {
                      e.target.src = 'https://placehold.co/600x400/f4712f/ffffff?text=Görsel+Bulunamadı';
                    }}
                  />
                </div>
                <button
                  onClick={closeModal}
                  className="absolute top-8 right-8 bg-white bg-opacity-20 backdrop-blur-sm rounded-full p-3 hover:bg-opacity-30 transition duration-300 text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-8">
                <div className="flex items-center mb-6">
                  <div className="bg-brand-orange rounded-full p-2 mr-4">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex items-center text-sm text-brand-orange">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span>{formatDate(selectedDocument.createdAt || selectedDocument.created_at)}</span>
                  </div>
                </div>
                
                <h2 className="text-4xl font-bold text-brand-navy mb-6 break-words leading-tight">
                  {selectedDocument.title}
                </h2>
                
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 mb-6">
                  <div className="text-brand-gray text-lg leading-relaxed break-words whitespace-pre-line">
                    {selectedDocument.description || 'Bu belge için açıklama mevcut değil.'}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DocumentsPage; 